import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Home as HomeIcon } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useInView } from '../hooks/useInView';

const PropertyCard = ({ property, index = 0, showStatus = false }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price) + ' CHF';
  };

  const isSold = property.status === 'vendu';
  const isReserved = property.status === 'reserve';

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      <Card className={`group overflow-hidden h-full transition-shadow duration-300 ${
        isSold || isReserved ? 'cursor-default' : 'hover:shadow-xl cursor-pointer'
      } ${isSold ? 'opacity-75' : ''}`}>
        <div className="relative overflow-hidden bg-gray-200">
          {/* Placeholder while loading */}
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse">
              <div className="w-12 h-12 rounded-full border-2 border-[#8B2942]/30 border-t-[#8B2942] animate-spin"></div>
            </div>
          )}
          {/* Error placeholder */}
          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <span className="text-gray-400 text-sm">Image non disponible</span>
            </div>
          )}
          <img
            src={property.image}
            alt={property.title}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            className={`w-full h-56 object-cover transition-all duration-500 ${
              !isSold && !isReserved ? 'group-hover:scale-110' : ''
            } ${isSold ? 'opacity-60' : ''} ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
          {/* Status badge — diagonal ribbon top-left for "Réservé", standard badge otherwise */}
          {showStatus && isReserved && (
            <div className="absolute top-0 left-0 w-32 h-32 overflow-hidden pointer-events-none z-10" data-testid="reserve-ribbon">
              <div
                className="absolute bg-[#8B2942] text-white text-sm font-bold uppercase tracking-widest text-center shadow-lg"
                style={{
                  width: '180px',
                  padding: '8px 0',
                  transform: 'rotate(-45deg)',
                  left: '-50px',
                  top: '28px',
                }}
              >
                Réservé
              </div>
            </div>
          )}
          {showStatus && !isReserved && (
            <Badge className="absolute top-2 left-2 md:top-4 md:left-4 bg-[#8B2942] hover:bg-[#6d2033] text-white text-xs md:text-sm px-2 py-1 md:px-2.5 md:py-1">
              {isSold ? 'Vendu' : 'En vente'}
            </Badge>
          )}
          {!isSold && !isReserved && (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                <Button asChild variant="secondary" size="sm" className="w-full">
                  <Link to={`/bien/${property.id}`} className="flex items-center justify-center gap-2">
                    Voir le bien
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </>
          )}
        </div>
        <CardContent className="p-5">
          <h3 className={`font-semibold text-lg text-[#8B2942] mb-2 line-clamp-2 transition-colors duration-200 ${
            !isSold && !isReserved && 'group-hover:text-[#6d2033]'
          }`}>
            {property.title}
          </h3>
          <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{property.location}</span>
            </div>
            {!isSold && (
              <div className="flex items-center gap-1">
                <HomeIcon className="w-4 h-4" />
                <span>{property.rooms} pièces</span>
              </div>
            )}
          </div>
          {!isSold && (
            <div className="text-xl font-bold text-[#8B2942]">
              {formatPrice(property.price)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertyCard;
